import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import MarketingBackdrop from "../components/MarketingBackdrop";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import api from "../api/axios";
import { getClusterByLabel } from "../utils/newsClusters";

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await api.get("/api/blogs/");
      setBlogs(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch blogs:", err);
      setError("AI was unable to gather today's global news. Please try again later.");
      setLoading(false);
    }
  };

  const filteredBlogs = blogs.filter((blog) => {
    const matchSearch = blog.title.toLowerCase().includes(search.toLowerCase()) || 
                        blog.content.toLowerCase().includes(search.toLowerCase());
    const matchCategory = category === "All" || blog.category === category;
    return matchSearch && matchCategory;
  });

  const categories = ["All", ...new Set(blogs.map(b => b.category))];

  return (
    <div className="relative isolate min-h-screen bg-gray-50 dark:bg-[#060B12] transition-colors pb-20">
      <SEO
        path="/blogs"
        title="Market Briefings"
        description="Daily AI-generated market briefings from FinAI — concise takes on what moved markets, key earnings, and themes to watch."
      />
      <MarketingBackdrop />
      <Navbar />

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pt-32 animate-fadeIn">

        {/* Page Header */}
        <div className="text-center md:text-left mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100/80 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-sm font-bold tracking-wide uppercase mb-4 border border-blue-200 dark:border-blue-500/30">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
            Automated Intelligence
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-4">
            Daily Market <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Briefings</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl font-medium mb-3">
            Read AI-generated insights synthesized from thousands of global financial news articles.
          </p>
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 px-4 py-2 rounded-xl backdrop-blur-sm border border-gray-200 dark:border-gray-700">
            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            System sweeps news and publishes a new briefing daily at Midnight (12:00 AM)
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-10 p-2 rounded-3xl bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            </div>
            <input
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white/50 dark:bg-black/40 border-transparent focus:bg-white dark:focus:bg-black/60 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-all shadow-inner"
              placeholder="Search trending topics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="px-6 py-3.5 rounded-2xl bg-white/50 dark:bg-black/40 border-transparent focus:bg-white dark:focus:bg-black/60 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-gray-900 dark:text-gray-100 transition-all font-medium min-w-[160px] shadow-inner"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <div className="w-16 h-16 relative mb-6">
              <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Compiling Today's Briefing...</h3>
            <p className="text-gray-500 font-medium">FinAI is reading current global news and writing your blog.</p>
          </div>
        ) : error ? (
          <div className="p-6 bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 rounded-2xl backdrop-blur-md text-center max-w-2xl mx-auto">
             <p className="text-lg font-bold">{error}</p>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-20 opacity-60">
            <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300">No blogs found.</h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog, idx) => (
              <Link
                key={blog.id}
                to={`/blogs/${blog.slug}`}
                state={{ blogData: blog }} // Pass data directly
                className="group flex flex-col rounded-3xl bg-white/40 dark:bg-gray-900/40 backdrop-blur-2xl border border-white/60 dark:border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-blue-500/10 hover:-translate-y-2 transition-all duration-300 overflow-hidden relative"
              >
                {/* Image Header */}
                <div className="w-full h-48 sm:h-56 relative overflow-hidden bg-gray-200 dark:bg-gray-800">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div>
                  {blog.image_url ? (
                    <img 
                      src={blog.image_url} 
                      alt={blog.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { e.target.onerror = null; e.target.outerHTML = '<div class="w-full h-full bg-gradient-to-br from-blue-600 to-purple-700"></div>' }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600"></div>
                  )}
                  
                  {/* Badge */}
                  <div className="absolute bottom-4 left-4 z-20">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-extrabold tracking-wider uppercase bg-white/20 backdrop-blur-md text-white border border-white/30">
                      {idx === 0 ? "Latest Today" : (() => {
                        const ClusterIcon = getClusterByLabel(blog.category)?.Icon;
                        return (
                          <>
                            {ClusterIcon && <ClusterIcon className="w-3.5 h-3.5" />}
                            {blog.category}
                          </>
                        );
                      })()}
                    </span>
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  {/* Date & Time */}
                  <p className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3">
                    {new Date(blog.published_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    {" • "}
                    {new Date(blog.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute:'2-digit' })}
                  </p>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight mb-3 group-hover:text-blue-500 transition-colors">
                    {blog.title}
                  </h3>

                  {/* Description Snippet */}
                  <p className="text-gray-600 dark:text-gray-400 font-medium line-clamp-3 mb-6 flex-1 text-sm leading-relaxed">
                    {blog.content.substring(0, 150).replace(/[#*]/g, '')}...
                  </p>

                  <div className="mt-auto flex items-center text-sm font-bold text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform">
                    Read Briefing <span className="ml-2">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
